import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, MapPin } from 'lucide-react';

// Instagram Icon Component
const InstagramIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

// WhatsApp Icon Component
const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// Arrow Up Right Icon
const ArrowUpRight = () => (
  <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="7" y1="17" x2="17" y2="7"></line>
    <polyline points="7 7 17 7 17 17"></polyline>
  </svg>
);

const footerLinks = {
  services: [
    { name: 'Google Ads', href: '/services/google-ads' },
    { name: 'Google Tag Manager', href: '/services/google-tag-manager' },
    { name: 'Google Analytics', href: '/services/google-analytics' },
    { name: 'Free Audit', href: '/booking' },
  ],
  company: [
    { name: 'About Us', href: '/#about' },
    { name: 'FAQ', href: '/faq' },
    { name: 'Contact', href: '/#contact' },
    { name: 'Book a Call', href: '/booking' },
  ],
};

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();

  const handleLinkClick = (href, e) => {
    if (href.startsWith('/#')) {
      e.preventDefault();
      const id = href.replace('/#', '');
      navigate('/');
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'instant' });
        setTimeout(() => {
          const element = document.getElementById(id);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      }, 50);
    }
  };

  const handlePageNavigation = () => {
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }, 10);
  };

  return (
    <footer className="footer-gradient text-card relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-google-blue rounded-full filter blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-google-green rounded-full filter blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block" onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}>
              <img 
                src="https://customer-assets.emergentagent.com/job_booking-marketyze/artifacts/zx1mwvxg_Marketyze%20white%20logo%20%283%29.png"
                alt="MARKETYZE - Google Ads & Analytics Agency"
                width="180"
                height="40"
                loading="lazy"
                className="h-10 w-auto"
              />
            </Link>
            <p className="mt-4 text-card/70 text-sm leading-relaxed max-w-xs">
              Expert Google Ads & Analytics consulting from former Google specialists. 
              We help businesses grow with data-driven decisions.
            </p>
            <div className="flex gap-4 mt-6">
              <a 
                href="https://www.instagram.com/_marketyze/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-card/10 flex items-center justify-center text-card/70 hover:bg-google-red/20 hover:text-google-red transition-all duration-300"
              >
                <InstagramIcon />
              </a>
              <a 
                href="https://api.whatsapp.com/send/?phone=%2B351913778029&text&type=phone_number&app_absent=0" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-card/10 flex items-center justify-center text-card/70 hover:bg-google-green/20 hover:text-google-green transition-all duration-300"
              >
                <WhatsAppIcon />
              </a>
            </div>
          </div>

          {/* Services Links */}
          <div>
            <h4 className="text-sm font-semibold text-card uppercase tracking-wider mb-4">
              Services
            </h4>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.href}
                    onClick={handlePageNavigation}
                    className="text-card/70 hover:text-card text-sm transition-colors duration-200 inline-flex items-center gap-1 group"
                  >
                    {link.name}
                    <span className="opacity-0 -translate-y-1 translate-x-1 group-hover:opacity-100 group-hover:translate-y-0 group-hover:translate-x-0 transition-all duration-200">
                      <ArrowUpRight />
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="text-sm font-semibold text-card uppercase tracking-wider mb-4">
              Company
            </h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  {link.href.startsWith('/#') ? (
                    <a 
                      href={link.href}
                      onClick={(e) => handleLinkClick(link.href, e)}
                      className="text-card/70 hover:text-card text-sm transition-colors duration-200 inline-flex items-center gap-1 group cursor-pointer"
                    >
                      {link.name}
                      <span className="opacity-0 -translate-y-1 translate-x-1 group-hover:opacity-100 group-hover:translate-y-0 group-hover:translate-x-0 transition-all duration-200">
                        <ArrowUpRight />
                      </span>
                    </a>
                  ) : (
                    <Link 
                      to={link.href}
                      onClick={handlePageNavigation}
                      className="text-card/70 hover:text-card text-sm transition-colors duration-200 inline-flex items-center gap-1 group"
                    >
                      {link.name}
                      <span className="opacity-0 -translate-y-1 translate-x-1 group-hover:opacity-100 group-hover:translate-y-0 group-hover:translate-x-0 transition-all duration-200">
                        <ArrowUpRight />
                      </span>
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-sm font-semibold text-card uppercase tracking-wider mb-4">
              Get in Touch
            </h4>
            <ul className="space-y-4">
              <li>
                <a 
                  href="mailto:growth@marketyze.eu"
                  className="text-card/70 hover:text-card text-sm transition-colors duration-200 flex items-center gap-3"
                >
                  <div className="w-8 h-8 rounded-full bg-card/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="h-4 w-4" />
                  </div>
                  growth@marketyze.eu
                </a>
              </li>
              <li className="flex items-center gap-3 text-card/70 text-sm">
                <div className="w-8 h-8 rounded-full bg-card/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="h-4 w-4" />
                </div>
                Europe, Remote
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-card/10">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-card/60 text-sm">
              © {currentYear} MARKETYZE. All rights reserved.
            </p>
            <div className="flex gap-6">
              <a href="#" className="text-card/60 hover:text-card text-sm transition-colors duration-200">
                Privacy Policy
              </a>
              <a href="#" className="text-card/60 hover:text-card text-sm transition-colors duration-200">
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
